import { Logo } from '@syncanix/ui';

import { FOOTER_LINK_GROUPS } from '@/components/chrome';
import { LocalizedLink as Link } from '@/components/LocalizedLink';
import { useT } from '@/core/I18nProvider';

import { BUILT_BY_LINE_KEY, COPYRIGHT_KEY, TRUST_BADGES } from './footer-links';

import type { ChromeFooterLink } from '@/components/chrome';
import type { Translator } from '@/i18n/translator';
import type { ReactElement } from 'react';

const FOOTER_COLUMNS = FOOTER_LINK_GROUPS.site;

export interface FooterProps {
  /**
   * The year to render in the copyright line. Defaults to the build-time year
   * (`__SYNCANIX_BUILD_YEAR__`, injected by Vite) — NOT a render-time
   * `new Date()` — so the static prerender and the hydrating client render the
   * same value and never trip a hydration mismatch across a New-Year boundary
   * (see ADR-0054). Tests pass an explicit value for a deterministic assertion.
   */
  readonly year?: number;
}

/**
 * Site-wide marketing footer (Phase 7 Pass 1 iter 269).
 *
 * Mounts on every route via `App.tsx`. 4-column nav grid + trust
 * badges + copyright. Links sourced from `./footer-links.ts`.
 *
 * External links open in a new tab with `rel="noopener noreferrer"`;
 * internal links use the locale-aware `<Link>` (LocalizedLink) so the SPA stays
 * single-page AND a non-English visitor's footer points at their locale's pages
 * (ADR-0133).
 */
export function Footer({ year }: FooterProps = {}): ReactElement {
  const { t } = useT();
  const renderedYear = year ?? __SYNCANIX_BUILD_YEAR__;
  return (
    <footer
      role="contentinfo"
      aria-label={t('footer.siteFooter')}
      className="relative border-t border-[color:var(--bd-line)] bg-[color:var(--bd-sunk)] px-5 pb-10 pt-14 sm:px-8"
    >
      <div className="mx-auto w-full max-w-[var(--bd-maxw)]">
        {/* Five tracks, because the nav has FOUR columns. It previously
            declared three, so `company` wrapped to a second row and left a
            ~350px void under `resources` on every route — a layout bug that
            reads as a design choice, which is why it survived. The brand
            column joins the row only at `lg`: five tracks at 768px would
            squeeze each nav column to ~104px and wrap "Data subject requests"
            across three lines in every locale. */}
        <div className="grid gap-9 lg:grid-cols-[1.4fr_repeat(4,1fr)]">
          <div>
            <Link
              to="/"
              aria-label={t('header.brandHome')}
              className="flex items-center gap-2.5 text-[1.06rem] font-semibold tracking-[-0.03em] text-[color:var(--bd-ink)] transition hover:opacity-80"
            >
              <Logo size={26} />
              syncanix
            </Link>
            <p className="mt-3.5 max-w-[30ch] text-[13.5px] leading-[1.6] text-[color:var(--bd-subtle)]">
              {t('footer.promise')}
            </p>
          </div>
          <nav
            aria-label={t('footer.navigation')}
            className="col-span-full grid grid-cols-2 gap-x-6 gap-y-8 md:grid-cols-4 lg:col-span-4"
          >
            {FOOTER_COLUMNS.map((column) => (
              <div key={column.slug} data-footer-column={column.slug}>
                <h2 className="font-mono rtl:font-sans text-[10.5px] uppercase tracking-[0.13em] text-[color:var(--bd-faint)] rtl:normal-case rtl:tracking-normal">
                  {t(column.headingKey)}
                </h2>
                <ul className="mt-3.5 flex flex-col gap-2.5 text-[13.5px] text-[color:var(--bd-body)]">
                  {column.links.map((link) => (
                    <li key={link.labelKey}>
                      <FooterAnchor link={link} t={t} />
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>
        </div>

        <div className="mt-11 flex flex-col gap-4 border-t border-[color:var(--bd-line)] pt-6 sm:flex-row sm:items-center sm:justify-between">
          <ul
            aria-label={t('footer.trustBadges')}
            className="flex flex-wrap items-center gap-x-3 gap-y-2 text-[12.5px] text-[color:var(--bd-faint)]"
          >
            {TRUST_BADGES.map((badge, idx) => (
              <li key={badge.labelKey} className="flex items-center gap-3">
                {idx > 0 ? (
                  <span aria-hidden="true" className="text-[color:var(--bd-subtle)]">
                    ·
                  </span>
                ) : null}
                <span className="text-[color:var(--bd-subtle)]">{t(badge.labelKey)}</span>
              </li>
            ))}
          </ul>
          <p className="text-[12.5px] text-[color:var(--bd-faint)]">
            <span>{t(BUILT_BY_LINE_KEY)}</span>
            <span aria-hidden="true" className="mx-2 text-[color:var(--bd-subtle)]">
              ·
            </span>
            <span>{t(COPYRIGHT_KEY, { year: renderedYear })}</span>
          </p>
        </div>
      </div>
    </footer>
  );
}

function FooterAnchor({
  link,
  t,
}: {
  readonly link: ChromeFooterLink;
  readonly t: Translator;
}): ReactElement {
  const baseClass =
    'transition-colors hover:text-[color:var(--bd-ink)] focus-visible:text-[color:var(--bd-ink)] focus-visible:outline-none focus-visible:underline';
  if (link.kind === 'external') {
    return (
      <a href={link.href} target="_blank" rel="noopener noreferrer" className={baseClass}>
        {t(link.labelKey)}
      </a>
    );
  }
  // LocalizedLink prefixes the active locale for every internal route, so the
  // href is passed through as-is (no manual per-link locale handling needed).
  return (
    <Link to={link.href} className={baseClass}>
      {t(link.labelKey)}
    </Link>
  );
}
